<template>
  <el-dialog title="选择图片 Choose Image" :visible.sync="innerVisible" width="900px" @open="handleOpen">
    <div class="picker-toolbar">
      <el-input
        v-model.trim="filters.keyword"
        clearable
        placeholder="搜索名称/地址 Search name/url"
        size="small"
        @keyup.enter.native="handleFilterChange"
        @clear="handleFilterChange"
      />
      <el-select v-model="filters.module" clearable placeholder="模块 Module" size="small" @change="handleFilterChange" @clear="handleFilterChange">
        <el-option
          v-for="item in moduleOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-button size="small" @click="handleFilterChange">查询 Search</el-button>
      <el-upload action="" :http-request="handleUpload" :show-file-list="false" accept="image/*">
        <el-button size="small" type="primary" :loading="uploading">上传并使用 Upload & Use</el-button>
      </el-upload>
    </div>

    <div class="picker-grid" v-loading="loading">
      <button
        v-for="item in assets"
        :key="item.id"
        type="button"
        class="picker-card"
        @click="handleSelect(item)"
      >
        <el-image :src="assetUrl(item.fileUrl)" :preview-src-list="[assetUrl(item.fileUrl)]" :alt="item.name" fit="cover" @click.native.stop />
        <span>{{ item.name }}</span>
        <em>{{ item.module || '未分组 Uncategorized' }}</em>
      </button>
      <el-empty v-if="!loading && assets.length === 0" description="暂无图片 No images yet" />
    </div>

    <div class="picker-pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :current-page="pagination.page"
        :page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>
  </el-dialog>
</template>

<script>
import { fetchMediaAssets, resolveAssetUrl, uploadMediaAsset } from '../api/media'

export default {
  name: 'MediaPickerDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    uploadModule: {
      type: String,
      default: 'media'
    }
  },
  data () {
    return {
      assets: [],
      filters: {
        keyword: '',
        module: ''
      },
      moduleOptions: [
        { label: '产品 Product', value: 'product' },
        { label: '分类 Category', value: 'category' },
        { label: '案例 Case', value: 'case' },
        { label: '文章 Article', value: 'article' },
        { label: '页面区块 Page Section', value: 'page-section' },
        { label: '媒体库 Media', value: 'media' }
      ],
      loading: false,
      uploading: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    innerVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  methods: {
    assetUrl: resolveAssetUrl,
    mediaQueryParams () {
      return {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        keyword: this.filters.keyword || undefined,
        module: this.filters.module || undefined
      }
    },
    handleOpen () {
      this.loadData()
    },
    async loadData () {
      this.loading = true
      try {
        const { data } = await fetchMediaAssets(this.mediaQueryParams())
        this.assets = Array.isArray(data) ? data : data.items
        this.pagination.total = Array.isArray(data) ? data.length : data.total
      } catch (error) {
        this.$message.error('媒体库加载失败 Failed to load media.')
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange (page) {
      this.pagination.page = page
      this.loadData()
    },
    handlePageSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.loadData()
    },
    handleSelect (item) {
      this.$emit('select', item)
      this.innerVisible = false
    },
    async handleUpload ({ file }) {
      if (this.uploading) return
      this.uploading = true
      try {
        const { data } = await uploadMediaAsset(file, this.uploadModule)
        this.assets = [data].concat(this.assets.filter(item => item.id !== data.id))
        this.$message.success('图片已上传 Image uploaded.')
        this.handleSelect(data)
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '上传失败 Upload failed.')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.picker-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.picker-toolbar .el-input,
.picker-toolbar .el-select {
  width: 220px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  min-height: 260px;
}

.picker-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 14px;
}

.picker-card {
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  text-align: left;
  cursor: pointer;
}

.picker-card .el-image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: #f2eee8;
}

.picker-card span,
.picker-card em {
  display: block;
  overflow: hidden;
  padding: 8px 10px 0;
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-card em {
  padding: 3px 10px 9px;
  color: var(--muted);
  font-size: 12px;
  font-style: normal;
}

@media (max-width: 720px) {
  .picker-toolbar .el-input,
  .picker-toolbar .el-select {
    width: 100%;
  }
}
</style>
